// Browsers normally cap GIF frames at ~20 ms minimum to prevent runaway
// playback for files that specify 0-delay frames; mirror that here.
const MIN_FRAME_DELAY_MS = 20;
const FALLBACK_FRAME_DELAY_MS = 100;

const CAN_DECODE_GIF_NATIVELY = typeof ImageDecoder !== 'undefined';

// Some browsers (notably Firefox) expose ImageDecoder but its VideoFrames
// fail at drawImage time. Once we observe that, skip the decoder path for
// the rest of the session so we don't replay partial frames before falling
// back on every subsequent image.
let decoderPathBroken = false;

export interface PlayGifInCanvasOptions {
  maxLoops: number;
  /** Called when the decoder path fails (non-abort) so callers can log. */
  onDecoderFailure?: (error: unknown) => void;
  /** Called when the still-frame fallback also fails (non-abort). */
  onFatalFailure?: (error: unknown) => void;
}

/**
 * Plays a GIF on the canvas with reduced-motion semantics: decode via
 * ImageDecoder for `maxLoops` loops then leave the last frame on the canvas.
 * Falls back to a single still frame if decoding is unavailable or fails.
 * Returns true if anything was rendered, false if aborted before any draw.
 */
export async function playGifInCanvas(
  canvas: HTMLCanvasElement,
  src: string,
  signal: AbortSignal,
  options: PlayGifInCanvasOptions,
): Promise<boolean> {
  if (CAN_DECODE_GIF_NATIVELY && !decoderPathBroken) {
    try {
      await playGifWithDecoder(canvas, src, signal, options.maxLoops);
      return true;
    } catch (error) {
      if (signal.aborted) {
        return false;
      }
      // Firefox exposes ImageDecoder but its VideoFrames fail at drawImage
      // ("frame is 'broken'"). Mark the path broken so subsequent images
      // skip it and go straight to the still-frame fallback.
      decoderPathBroken = true;
      options.onDecoderFailure?.(error);
    }
  }

  try {
    await renderStillFrame(canvas, src, signal);
    return true;
  } catch (error) {
    if (!signal.aborted) {
      options.onFatalFailure?.(error);
    }
    return false;
  }
}

export function isGif(src: string, mimeType?: string): boolean {
  if (mimeType?.toLowerCase() === 'image/gif') {
    return true;
  }
  if (!src) {
    return false;
  }
  const lower = src.toLowerCase();

  if (lower.startsWith('data:image/gif')) {
    return true;
  }

  const path = lower.split('?')[0].split('#')[0];
  return path.endsWith('.gif');
}

export async function playGifWithDecoder(
  canvas: HTMLCanvasElement,
  src: string,
  signal: AbortSignal,
  maxLoops: number,
): Promise<void> {
  const response = await fetch(src, { signal });
  if (!response.ok) {
    throw new Error(`GIF fetch failed: ${response.status}`);
  }
  if (!response.body) {
    throw new Error('GIF response has no body');
  }

  const decoder = new ImageDecoder({ data: response.body, type: 'image/gif' });

  try {
    // tracks.ready resolves after the GIF header is parsed, but frameCount
    // grows as more of the stream is consumed. completed resolves once all
    // input bytes have been parsed, so frameCount reflects the full GIF.
    await decoder.completed;
    if (signal.aborted) {
      return;
    }

    const track = decoder.tracks.selectedTrack;
    if (!track) {
      throw new Error('GIF has no selected track');
    }

    const { frameCount } = track;
    let ctx: CanvasRenderingContext2D | null = null;

    for (let loop = 0; loop < maxLoops; loop++) {
      for (let i = 0; i < frameCount; i++) {
        const result = await decoder.decode({ frameIndex: i });
        if (signal.aborted) {
          result.image.close();
          return;
        }

        if (!ctx) {
          canvas.width = result.image.codedWidth;
          canvas.height = result.image.codedHeight;
          ctx = canvas.getContext('2d');
          if (!ctx) {
            result.image.close();
            throw new Error('Canvas 2D context unavailable');
          }
        }

        // Clear before each frame: GIF frames with transparent regions don't
        // overwrite previous pixels, so without this, frames layer on top of
        // each other (visible as e.g. multiple tails on an animated sticker).
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(result.image, 0, 0);
        const durationUs = result.image.duration;
        const rawMs = durationUs && durationUs > 0 ? durationUs / 1000 : FALLBACK_FRAME_DELAY_MS;
        const delayMs = Math.max(MIN_FRAME_DELAY_MS, rawMs);
        result.image.close();
        await sleep(delayMs, signal);
      }
    }
  } finally {
    decoder.close();
  }
}

export function renderStillFrame(
  canvas: HTMLCanvasElement,
  src: string,
  signal: AbortSignal,
): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    if (signal.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }

    const img = new Image();

    const cleanup = () => {
      img.removeEventListener('load', onLoad);
      img.removeEventListener('error', onError);
      signal.removeEventListener('abort', onAbort);
    };

    const onLoad = () => {
      cleanup();
      if (signal.aborted) {
        reject(new DOMException('Aborted', 'AbortError'));
        return;
      }

      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Canvas 2D context unavailable'));
        return;
      }

      try {
        ctx.drawImage(img, 0, 0);
      } catch (error) {
        reject(error);
        return;
      }

      resolve();
    };

    const onError = () => {
      cleanup();
      reject(new Error('Image failed to load'));
    };

    const onAbort = () => {
      cleanup();
      img.src = '';
      reject(new DOMException('Aborted', 'AbortError'));
    };

    img.addEventListener('load', onLoad);
    img.addEventListener('error', onError);
    signal.addEventListener('abort', onAbort);

    img.src = src;
  });
}

function sleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    if (signal.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }

    const timeoutId = setTimeout(() => {
      signal.removeEventListener('abort', onAbort);
      resolve();
    }, ms);

    const onAbort = () => {
      clearTimeout(timeoutId);
      reject(new DOMException('Aborted', 'AbortError'));
    };

    signal.addEventListener('abort', onAbort);
  });
}
